import { ReactNode, RefObject } from 'react';
import { useSwipe, type SwipeDir } from '../lib/useSwipe';

interface Props {
  onCommit: (dir: SwipeDir) => void;
  canCommit?: (dir: SwipeDir) => boolean;
  rtl?: boolean;
  disabled?: boolean;
  /** Decorative stamp labels shown while dragging/flinging towards that
   * side — e.g. "On my mind" / "Not really". Omit a side to show no stamp
   * for it (the mood card only ever commits forward). */
  stampForward?: string;
  stampBack?: string;
  children: ReactNode;
  className?: string;
  /** Imperative access for a parent's own buttons/keyboard handler. */
  swipeRef?: (api: { fling: (dir: SwipeDir) => void }) => void;
}

// The visual half of the shared swipe engine (useSwipe does the gesture
// math): a positioned wrapper with two decorative stamps that fade in with
// drag distance, exactly mirroring whichever button the student could have
// tapped instead. Never the only way to answer — canCommit/fling are also
// wired to real buttons and keys by the caller.
export function SwipeCard({ onCommit, canCommit, rtl, disabled, stampForward, stampBack, children, className, swipeRef }: Props) {
  const swipe = useSwipe({ onCommit, canCommit, rtl, disabled });
  if (swipeRef) swipeRef({ fling: swipe.fling });

  return (
    <div
      ref={swipe.ref as RefObject<HTMLDivElement>}
      className={`sx-swipe-card${className ? ` ${className}` : ''}`}
      style={swipe.style}
      {...swipe.handlers}
    >
      {stampForward && (
        <span className="sx-stamp yes" aria-hidden="true" style={{ opacity: swipe.stamp.dir === 'forward' ? swipe.stamp.progress : 0 }}>
          {stampForward}
        </span>
      )}
      {stampBack && (
        <span className="sx-stamp no" aria-hidden="true" style={{ opacity: swipe.stamp.dir === 'back' ? swipe.stamp.progress : 0 }}>
          {stampBack}
        </span>
      )}
      {children}
    </div>
  );
}
